/**
 * UniversalEntityForm - Schema-Driven Forms for Federated Subgraphs (Issue #658)
 *
 * A single component that renders forms for ANY subgraph entity type by:
 * 1. Querying the form schema from the backend
 * 2. Converting it to FormDefinition (for existing FormRenderer)
 * 3. Optionally loading entity data for edit mode
 *
 * ## Architecture
 *
 * ```
 * UniversalEntityForm
 *   -> useFormSchema() query
 *   -> schemaToFormDefinition() converter
 *   -> FormRenderer (existing 28+ field types)
 * ```
 *
 * ## Life-Safety Features
 *
 * - CRITICAL/LIFE_CRITICAL forms show confirmation dialogs
 * - PII fields are audited on change
 * - Safety level badges displayed
 * - Dual approval flow for life-critical forms
 *
 * ## Usage
 *
 * ```tsx
 * // Create new Patient
 * <UniversalEntityForm
 *   subgraph="fhir"
 *   entityType="Patient"
 *   onSubmit={handleSubmit}
 * />
 *
 * // Edit existing Worker
 * <UniversalEntityForm
 *   subgraph="payroll"
 *   entityType="Worker"
 *   entityId="worker_123"
 *   onSubmit={handleUpdate}
 * />
 * ```
 */

import React, { useMemo, useCallback, useState } from 'react';
import {
  Stack,
  Alert,
  Skeleton,
  Badge,
  Group,
  Text,
  Modal,
  Button,
} from '@mantine/core';
import { IconAlertTriangle, IconShieldCheck } from '@tabler/icons-react';

import { FormRenderer } from '@/renderer';
import type { FormRendererProps } from '@/renderer/types';
import { useFormSchema, useEntityData } from './useFormSchema';
import { schemaToFormDefinition, mergeWithEntityData } from './schemaToFormDef';

// =============================================================================
// TYPES
// =============================================================================

export interface UniversalEntityFormProps {
  /** Subgraph name (e.g., "fhir", "payroll", "ofbiz") */
  subgraph: string;
  /** Entity type (e.g., "Patient", "Worker", "Invoice") */
  entityType: string;
  /** Entity ID for edit mode (optional) */
  entityId?: string;
  /** Callback when form is submitted successfully */
  onSubmit?: (data: Record<string, unknown>) => void | Promise<void>;
  /** Callback when user cancels */
  onCancel?: () => void;
  /** Custom field renderers (optional) */
  customRenderers?: FormRendererProps['customRenderers'];
  /** Additional initial values to merge (optional) */
  initialValues?: Record<string, unknown>;
  /** Override form title (optional) */
  title?: string;
}

// =============================================================================
// SAFETY LEVEL UI CONFIG
// =============================================================================

const SAFETY_LEVEL_CONFIG = {
  standard: {
    color: 'gray',
    icon: null,
    label: 'Standard',
  },
  elevated: {
    color: 'yellow',
    icon: IconAlertTriangle,
    label: 'Elevated',
  },
  critical: {
    color: 'orange',
    icon: IconAlertTriangle,
    label: 'Critical',
  },
  'life-critical': {
    color: 'red',
    icon: IconShieldCheck,
    label: 'Life-Critical',
  },
};

// =============================================================================
// COMPONENT
// =============================================================================

export function UniversalEntityForm({
  subgraph,
  entityType,
  entityId,
  onSubmit,
  onCancel,
  customRenderers,
  initialValues,
  title,
}: UniversalEntityFormProps) {
  // State for confirmation dialog
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingSubmitData, setPendingSubmitData] = useState<Record<
    string,
    unknown
  > | null>(null);

  // Query form schema
  const {
    schema,
    loading: schemaLoading,
    error: schemaError,
  } = useFormSchema({
    subgraph,
    entityType,
  });

  // Convert schema to form definition
  const formDefinition = useMemo(() => {
    if (!schema) return null;
    return schemaToFormDefinition(schema);
  }, [schema]);

  // Query entity data for edit mode using the data-layer hook
  const { data: entityData, loading: entityLoading } = useEntityData({
    queryName: formDefinition?.metadata?.fetchQuery as string | undefined,
    entityId,
    skip: !formDefinition?.metadata?.fetchQuery || !entityId,
  });

  // Merge entity data into form definition
  const formWithData = useMemo(() => {
    if (!formDefinition) return null;

    let merged = formDefinition;

    // Apply initial values prop
    if (initialValues) {
      merged = mergeWithEntityData(merged, initialValues);
    }

    // Apply entity data for edit mode
    if (entityData && entityId) {
      merged = mergeWithEntityData(merged, entityData);
    }

    return merged;
  }, [formDefinition, entityData, entityId, initialValues]);

  // Handle form submission
  const handleSubmit = useCallback(
    async (values: Record<string, unknown>) => {
      if (!formWithData) return;

      const safetyLevel = formWithData.safetyLevel;

      // For critical/life-critical, show confirmation first
      if (
        (safetyLevel === 'critical' || safetyLevel === 'life-critical') &&
        !showConfirmation
      ) {
        setPendingSubmitData(values);
        setShowConfirmation(true);
        return;
      }

      // Proceed with submission
      try {
        await onSubmit?.(values);
      } catch (error) {
        console.error('Form submission failed:', error);
        throw error;
      }
    },
    [formWithData, onSubmit, showConfirmation]
  );

  // Handle confirmation dialog confirm
  const handleConfirm = useCallback(async () => {
    if (!pendingSubmitData) return;

    setShowConfirmation(false);
    try {
      await onSubmit?.(pendingSubmitData);
    } catch (error) {
      console.error('Form submission failed:', error);
    }
    setPendingSubmitData(null);
  }, [pendingSubmitData, onSubmit]);

  // Handle confirmation dialog cancel
  const handleConfirmCancel = useCallback(() => {
    setShowConfirmation(false);
    setPendingSubmitData(null);
  }, []);

  // Loading state
  if (schemaLoading || entityLoading) {
    return (
      <Stack gap="md">
        <Skeleton height={40} />
        <Skeleton height={60} />
        <Skeleton height={60} />
        <Skeleton height={60} />
        <Skeleton height={40} width={120} />
      </Stack>
    );
  }

  // Error state
  if (schemaError) {
    return (
      <Alert
        color="red"
        title="Failed to load form"
        icon={<IconAlertTriangle />}
      >
        Could not load form schema for {subgraph}/{entityType}.{' '}
        {schemaError.message}
      </Alert>
    );
  }

  // No schema found
  if (!formWithData) {
    return (
      <Alert
        color="yellow"
        title="Form not available"
        icon={<IconAlertTriangle />}
      >
        No form schema defined for {subgraph}/{entityType}. The subgraph may
        need to expose a form schema for this entity type.
      </Alert>
    );
  }

  // Get safety level config
  const safetyConfig =
    SAFETY_LEVEL_CONFIG[formWithData.safetyLevel ?? 'standard'];

  return (
    <Stack gap="md">
      {/* Header with safety badge */}
      <Group justify="space-between">
        <Text fw={600} size="lg">
          {title ?? formWithData.name}
        </Text>
        {formWithData.safetyLevel &&
          formWithData.safetyLevel !== 'standard' && (
            <Badge
              color={safetyConfig.color}
              leftSection={
                safetyConfig.icon && <safetyConfig.icon size={14} />
              }
            >
              {safetyConfig.label}
            </Badge>
          )}
      </Group>

      {/* Life-critical warning */}
      {formWithData.safetyLevel === 'life-critical' && (
        <Alert color="red" icon={<IconShieldCheck />}>
          This form affects patient safety or financial integrity. All changes
          are audited and may require dual approval.
        </Alert>
      )}

      {/* Form renderer */}
      <FormRenderer
        form={formWithData}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        customRenderers={customRenderers}
      />

      {/* Confirmation modal for critical operations */}
      <Modal
        opened={showConfirmation}
        onClose={handleConfirmCancel}
        title={
          formWithData.safetyLevel === 'life-critical'
            ? 'Life-Critical Operation'
            : 'Confirm Submission'
        }
        centered
      >
        <Stack gap="md">
          <Text>
            {formWithData.safetyLevel === 'life-critical'
              ? 'This action may affect patient safety or financial integrity. Please verify all information is correct before proceeding.'
              : 'Are you sure you want to submit this form?'}
          </Text>

          {formWithData.dualApproval && (
            <Alert color="orange" icon={<IconAlertTriangle />}>
              This operation requires dual approval. After submission, another
              authorized user must approve before changes take effect.
            </Alert>
          )}

          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={handleConfirmCancel}>
              Cancel
            </Button>
            <Button
              color={
                formWithData.safetyLevel === 'life-critical' ? 'red' : 'blue'
              }
              onClick={handleConfirm}
            >
              {formWithData.dualApproval ? 'Submit for Approval' : 'Confirm'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

export default UniversalEntityForm;
